import { formatDate2, formatDateTime } from "../algorithms/date";
import { findLockerName } from "./find-locker-by-id";

export function computeAggregateFromBackend(
  rawRows,
  aggregateData,
  nameData,
  // findLockerName,
  options = {},
) {
  console.log({ rawRows });
  /**
   * This simplified version works with backend-calculated aggregations
   * rawRows: individual transactions (from resolve_overstay_fees)
   * aggregateData: aggregated data (from resolve_aggregate_overstay_fees)
   */

  const config = {
    statusMapping: {
      Failed: "failed",
      "-1": "refunded",
      0: "failed",
      1: "success",
      ...options.statusMapping,
    },
    includeRefunds: options.includeRefunds !== false,
    includeDuplicates: options.includeDuplicates !== false,
    customStatuses: options.customStatuses || [],
    forAggregate: options.forAggregate,
    ...options,
  };

  let whicData = options.forAggregate ? aggregateData : rawRows;
  // Transform individual records for table display (minimal processing)
  const tableData = whicData.map((tx) => ({
    packageStateNo: tx.packageStateNo,
    serviceType: tx.serviceType, // Already calculated by backend
    partnerName: tx.partnerId ? tx.partnerId : "-",
    status: tx.status,
    paymentCurrency: tx.paymentCurrency === "PHP" ? "PHP" : "MBOX COINS",
    lockerId: tx.lockerId,
    lockerName: findLockerName(tx.lockerId, nameData),
    paymentAmount: tx.paymentAmount,
    totalTransactions: tx.totalTransactions,
    paymentGateway: tx.paymentCurrency === "PHP" ? "AUB" : "MBOX",
    senderMobile:
      tx.packageStateNo >= 300 && tx.packageStateNo <= 328
        ? tx.senderMobile
        : tx.partnerAgentMobile,
    recipientMobile: tx.recipientMobile || "-",
    waybillNumber:
      tx.packageStateNo >= 300 && tx.packageStateNo <= 328
        ? "(Storage)"
        : tx.waybillNumber,
    excessFee: tx.excessFee || 0,
    paymentId: tx.paymentId,
    packageId: tx.packageId,
    unformattedPaymentDate: tx.paymentDate,
    paymentDate: formatDate2(tx.paymentDate),
    userAccountId: tx.userAccountId,
    userId: tx.userId,
    senderId: tx.senderId,
    partnerAgentId: tx.partnerAgentId,
    duplicate: tx.duplicate,
    duplicateCount: tx.duplicateCount || 0,
    recipientId: tx.recipientId,
    transactionFee: tx.transactionFee,
    packagePrice: tx.packagePrice,
    doorsizeId: tx.doorSizeId
      ? doorSizeOptions.find(
          (x) => parseInt(x.value) === parseInt(tx.doorSizeId),
        )?.label
      : null,
    doorSize: tx.doorSize || null,
    storageDuration: tx.hours || null,
    totalPaymentAmount: tx.totalPaymentAmount,
  }));

  // Calculate totals directly from aggregateData
  const totalAmt = calculateTotalAmounts(whicData, options.forAggregate);

  // Transform aggregated data into status-by-service structure
  const statusBySvc = buildStatusByServiceFromAggregate(
    whicData,
    options.forAggregate,
  );

  // Build transaction counts from aggregate data
  const txnCounts = buildTransactionCounts(whicData, options.forAggregate);

  // Calculate gross sales from aggregate data
  const grossSales = calculateGrossSales(whicData, options.forAggregate);
  return {
    tableData,
    totalAmt,
    statusBySvc,
    totalTxns: txnCounts,
    grossSales,
    config,
    // Include raw backend data for advanced usage
    rawAggregateData: aggregateData,
  };
}

function calculateTotalAmounts(aggregateData, isAggregate) {
  /**
   * Calculate total amounts by currency from aggregate data
   */

  const totals = aggregateData.reduce(
    (acc, item) => {
      let payment = isAggregate ? item.totalPaymentAmount : item.paymentAmount;

      if (item.paymentCurrency === "PHP") {
        acc.php += parseFloat(payment || 0);
      } else {
        acc.coins += parseFloat(payment || 0);
      }
      return acc;
    },
    { php: 0, coins: 0 },
  );

  return {
    php: totals.php.toFixed(2),
    coins: totals.coins.toFixed(2),
  };
}

function buildStatusByServiceFromAggregate(data, isAggregate) {
  /**
   * Transform data into the expected status-by-service structure
   * Works with both aggregate and raw data
   */
  const statusBySvc = {
    success: { storage: { php: 0, coins: 0 }, dropOff: { php: 0, coins: 0 } },
    failed: { storage: { php: 0, coins: 0 }, dropOff: { php: 0, coins: 0 } },
    refunded: { storage: { php: 0, coins: 0 }, dropOff: { php: 0, coins: 0 } },
    duplicates: {
      storage: { php: 0, coins: 0 },
      dropOff: { php: 0, coins: 0 },
    },
  };

  data.forEach((item) => {
    // Determine service type
    const serviceKey = isAggregate
      ? item.serviceType === "Storage"
        ? "storage"
        : "dropOff"
      : item.packageStateNo >= 300 && item.packageStateNo <= 328
        ? "storage"
        : "dropOff";

    const currencyKey = item.paymentCurrency === "PHP" ? "php" : "coins";

    // Get payment amount based on data type
    const paymentAmount = isAggregate
      ? parseFloat(item.totalPaymentAmount || 0)
      : parseFloat(item.paymentAmount || 0);

    // Map payment status to our status categories
    let statusCategory = "success"; // default
    if (item.status === "-1") statusCategory = "refunded";
    else if (item.status === "0" || item.status === "Failed")
      statusCategory = "failed";
    else if (isAggregate ? item.hasDuplicates : item.duplicate)
      statusCategory = "duplicates";

    if (statusBySvc[statusCategory]) {
      statusBySvc[statusCategory][serviceKey][currencyKey] += paymentAmount;
    }
  });

  // Format and add subtotals
  Object.keys(statusBySvc).forEach((status) => {
    const svc = statusBySvc[status];
    statusBySvc[status] = {
      storage: {
        php: svc.storage.php.toFixed(2),
        coins: svc.storage.coins.toFixed(2),
      },
      dropOff: {
        php: svc.dropOff.php.toFixed(2),
        coins: svc.dropOff.coins.toFixed(2),
      },
      subtotal: {
        php: (svc.storage.php + svc.dropOff.php).toFixed(2),
        coins: (svc.storage.coins + svc.dropOff.coins).toFixed(2),
      },
    };
  });

  return statusBySvc;
}

function buildTransactionCounts(data, isAggregate) {
  /**
   * Build transaction count structure from both aggregate and raw data
   */
  const txnCounts = {
    total: { php: 0, coins: 0 },
    success: { php: 0, coins: 0 },
    failed: { php: 0, coins: 0 },
    refunded: { php: 0, coins: 0 },
    duplicates: { php: 0, coins: 0 },
  };

  data.forEach((item) => {
    const currencyKey = item.paymentCurrency === "PHP" ? "php" : "coins";

    // Get transaction count based on data type
    const transactions = isAggregate
      ? item.totalTransactions || 1 // Use totalTransactions from aggregate, fallback to 1
      : 1; // Each raw row represents 1 transaction

    let statusCategory = "success"; // default
    if (item.status === "-1") statusCategory = "refunded";
    else if (item.status === "0" || item.status === "Failed")
      statusCategory = "failed";
    else if (isAggregate ? item.hasDuplicates : item.duplicate)
      statusCategory = "duplicates";

    if (txnCounts[statusCategory]) {
      txnCounts[statusCategory][currencyKey] += transactions;
    }
    txnCounts.total[currencyKey] += transactions;
  });

  return txnCounts;
}

function calculateGrossSales(data, isAggregate) {
  /**
   * Calculate gross sales from both aggregate and raw data
   */
  const grossSales = {
    storage: { php: 0, coins: 0 },
    dropOff: { php: 0, coins: 0 },
  };

  data.forEach((item) => {
    // Determine service type
    const serviceKey = isAggregate
      ? item.serviceType === "Storage"
        ? "storage"
        : "dropOff"
      : item.packageStateNo >= 300 && item.packageStateNo <= 328
        ? "storage"
        : "dropOff";

    const currencyKey = item.paymentCurrency === "PHP" ? "php" : "coins";

    // Get payment amount based on data type
    const paymentAmount = isAggregate
      ? parseFloat(item.totalPaymentAmount || 0)
      : parseFloat(item.paymentAmount || 0);

    // Only count successful transactions for gross sales
    if (item.status === "1" || item.status === "success") {
      grossSales[serviceKey][currencyKey] += paymentAmount;
    }
  });

  // Format and add subtotals
  return {
    storage: {
      php: grossSales.storage.php.toFixed(2),
      coins: grossSales.storage.coins.toFixed(2),
    },
    dropOff: {
      php: grossSales.dropOff.php.toFixed(2),
      coins: grossSales.dropOff.coins.toFixed(2),
    },
    subtotal: {
      php: (grossSales.storage.php + grossSales.dropOff.php).toFixed(2),
      coins: (grossSales.storage.coins + grossSales.dropOff.coins).toFixed(2),
    },
  };
}

// Legacy function for backward compatibility - now just calls the new version
export function computeAggregate(
  rawRows,
  nameData,
  findLockerName,
  options = {},
) {
  console.warn(
    "computeAggregate is deprecated. Use computeAggregateFromBackend with backend-calculated data instead.",
  );

  // Fallback to original logic if no backend data provided
  return computeAggregateOriginal(rawRows, nameData, findLockerName, options);
}

function computeAggregateOriginal(
  rawRows,
  nameData,
  findLockerName,
  options = {},
) {
  /**
   * Original computeAggregate logic - kept for backward compatibility
   * This should be phased out as backend calculations are adopted
   */
  const config = {
    statusMapping: {
      Failed: "failed",
      "-1": "refunded",
      0: "failed",
      1: "success",
      ...options.statusMapping,
    },
    includeRefunds: options.includeRefunds !== false,
    includeDuplicates: options.includeDuplicates !== false,
    customStatuses: options.customStatuses || [],
    ...options,
  };

  // Map raw data to table-friendly objects
  const tableData = rawRows.map((tx) => ({
    packageStateNo: tx.packageStateNo,
    serviceType:
      tx.packageStateNo >= 300 && tx.packageStateNo <= 328
        ? "Storage"
        : "Drop-off",
    partnerName: tx.partnerId ? tx.partnerId : "-",
    status: tx.status,
    paymentCurrency: tx.paymentCurrency === "PHP" ? "PHP" : "MBOX COINS",
    lockerId: tx.lockerId,
    lockerName: findLockerName(tx.lockerId, nameData),
    paymentAmount: tx.paymentAmount,
    totalTransactions: tx.totalTransactions,
    paymentGateway: tx.paymentCurrency === "PHP" ? "AUB" : "MBOX",
    senderMobile:
      tx.packageStateNo >= 300 && tx.packageStateNo <= 328
        ? tx.senderMobile
        : tx.partnerAgentMobile,
    recipientMobile: tx.recipientMobile || "-",
    waybillNumber:
      tx.packageStateNo >= 300 && tx.packageStateNo <= 328
        ? "(Storage)"
        : tx.waybillNumber,
    excessFee: tx.excessFee ? tx.excessFee : 0,
    paymentId: tx.paymentId,
    packageId: tx.packageId,
    paymentDate: formatDateTime(tx.paymentDate),
    userAccountId: tx.userAccountId,
    userId: tx.userId,
    senderId: tx.senderId,
    partnerAgentId: tx.partnerAgentId,
    duplicate: tx.duplicate,
    duplicateCount: tx.duplicate ? tx.duplicateCount : 0,
    recipientId: tx.recipientId,
    transactionFee: tx.transactionFee,
    packagePrice: tx.packagePrice,
    doorsizeId: tx.doorSizeId
      ? doorSizeOptions.find(
          (x) => parseInt(x.value) === parseInt(tx.doorSizeId),
        )?.label
      : null,
    storageDuration: tx.hours ? tx.hours : null,
  }));

  // Sum total amounts by currency
  const totalAmt = tableData.reduce(
    (acc, { paymentCurrency, paymentAmount }) => {
      if (paymentCurrency === "PHP") acc.php += paymentAmount;
      else acc.coins += paymentAmount;
      return acc;
    },
    { php: 0, coins: 0 },
  );

  // Initialize dynamic status buckets
  const initServiceBucket = () => ({
    storage: { php: 0, coins: 0 },
    dropOff: { php: 0, coins: 0 },
  });

  // Create dynamic status totals based on all possible statuses
  const allStatuses = [
    "success",
    "failed",
    "duplicates",
    ...(config.includeRefunds ? ["refunded"] : []),
    ...config.customStatuses,
  ];

  const statusTotals = allStatuses.reduce((acc, status) => {
    acc[status] = initServiceBucket();
    return acc;
  }, {});

  // Process each transaction and categorize it
  tableData.forEach((item) => {
    const svcKey = item.serviceType === "Storage" ? "storage" : "dropOff";
    const curKey = item.paymentCurrency === "PHP" ? "php" : "coins";

    // Determine the status category
    let statusCategory;

    if (item.duplicate && config.includeDuplicates) {
      statusCategory = "duplicates";
    } else if (item.status === "-1" && config.includeRefunds) {
      statusCategory = "refunded";
    } else if (config.statusMapping[item.status]) {
      statusCategory = config.statusMapping[item.status];
    } else {
      // Default fallback
      statusCategory = item.status === "Failed" ? "failed" : "success";
    }

    // Add to the appropriate bucket if it exists
    if (statusTotals[statusCategory]) {
      statusTotals[statusCategory][svcKey][curKey] += item.paymentAmount;
    }
  });

  // Helper to format and compute subtotals
  const fmt = (n) => n.toFixed(2);
  const buildSvc = (svc) => ({
    storage: { php: fmt(svc.storage.php), coins: fmt(svc.storage.coins) },
    dropOff: { php: fmt(svc.dropOff.php), coins: fmt(svc.dropOff.coins) },
    subtotal: {
      php: fmt(svc.storage.php + svc.dropOff.php),
      coins: fmt(svc.storage.coins + svc.dropOff.coins),
    },
  });

  // Build final status-by-service object dynamically
  const statusBySvc = {};
  allStatuses.forEach((status) => {
    if (statusTotals[status]) {
      statusBySvc[status] = buildSvc(statusTotals[status]);
    }
  });

  // Initialize transaction count buckets
  const initTxnBucket = () => ({ php: 0, coins: 0 });
  const txnCounts = allStatuses.reduce(
    (acc, status) => {
      acc[status] = initTxnBucket();
      return acc;
    },
    { total: initTxnBucket() },
  );

  // Count transactions by category
  tableData.forEach((item) => {
    const curKey = item.paymentCurrency === "PHP" ? "php" : "coins";

    // Determine the status category (same logic as above)
    let statusCategory;

    if (item.duplicate && config.includeDuplicates) {
      statusCategory = "duplicates";
    } else if (item.status === "-1" && config.includeRefunds) {
      statusCategory = "refunded";
    } else if (config.statusMapping[item.status]) {
      statusCategory = config.statusMapping[item.status];
    } else {
      statusCategory = item.status === "Failed" ? "failed" : "success";
    }

    // Increment counts
    if (txnCounts[statusCategory]) {
      txnCounts[statusCategory][curKey]++;
    }
    txnCounts.total[curKey]++;
  });

  // Calculate gross sales (successful - refunded)
  const calculateGrossSales = () => {
    const successTotals = statusTotals.success || {
      storage: { php: 0, coins: 0 },
      dropOff: { php: 0, coins: 0 },
    };
    const refundedTotals = statusTotals.refunded || {
      storage: { php: 0, coins: 0 },
      dropOff: { php: 0, coins: 0 },
    };

    return {
      storage: {
        php: fmt(successTotals.storage.php - refundedTotals.storage.php),
        coins: fmt(successTotals.storage.coins - refundedTotals.storage.coins),
      },
      dropOff: {
        php: fmt(successTotals.dropOff.php - refundedTotals.dropOff.php),
        coins: fmt(successTotals.dropOff.coins - refundedTotals.dropOff.coins),
      },
      subtotal: {
        php: fmt(
          successTotals.storage.php +
            successTotals.dropOff.php -
            (refundedTotals.storage.php + refundedTotals.dropOff.php),
        ),
        coins: fmt(
          successTotals.storage.coins +
            successTotals.dropOff.coins -
            (refundedTotals.storage.coins + refundedTotals.dropOff.coins),
        ),
      },
    };
  };

  const grossSales = calculateGrossSales();

  return {
    tableData,
    totalAmt: { php: fmt(totalAmt.php), coins: fmt(totalAmt.coins) },
    statusBySvc,
    totalTxns: txnCounts,
    grossSales,
    config,
  };
}

export const doorSizeOptions = [
  { value: "1", label: "Small" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Large" },
];
